"use client";

import { Core } from "@faire/design-tokens";
import { IImage } from "@faire/web-api/indigofair/data/IImage";
import { debounceIfPossible } from "@faire/web/common/globals/debounceIfPossible";
import { getWindow } from "@faire/web/common/globals/getWindow";
import { IOptimizeImageOptions } from "@faire/web/common/images/optimizeImages";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { optimizeProductImageUrl } from "@faire/web/common/transform-images";
import { Opacity } from "@faire/web/slate/Color";
import { Row } from "@faire/web/slate/Layout";
import { Typography } from "@faire/web/slate/Typography";
import { TransparencyOverlay } from "@faire/web/ui/TransparencyOverlay";
import React, { useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";

import { useSettingDealsHub } from "@faire/retailer/settings/useSettingDealsHub";

const IMAGE_GRID_HEIGHT = 200;

interface IProps {
  images: IImage[];
  useLazyImageLoading?: boolean;
}

const TextOverlay = (props: {
  numAdditionalItems?: number;
  showOverlay?: boolean;
}) => {
  const { numAdditionalItems, showOverlay } = props;
  const isFlashDealsRevamp = useSettingDealsHub();

  if ((numAdditionalItems ?? 0) <= 0 || !showOverlay) {
    return null;
  }

  return (
    <Overlay>
      <OverlayTextContainer>
        <Typography variant="sectionHeaderSansMedium" color={Core.text.inverse}>
          {`${isFlashDealsRevamp ? `+` : `+ `}${(numAdditionalItems ?? 0) + 1}`}
        </Typography>
      </OverlayTextContainer>
    </Overlay>
  );
};

const getImageOptions = (
  numImages: number,
  isFirst: boolean,
  gridWidth: number
): IOptimizeImageOptions => {
  const option: Partial<IOptimizeImageOptions> = {
    resizeMethod: "fill",
    ignoreResizeTag: true,
  };
  if (numImages === 1) {
    return {
      resizeMethod: "pad",
      background: Core.surface.subdued,
      height: IMAGE_GRID_HEIGHT,
      width: gridWidth,
    };
  }
  if (numImages === 2) {
    return {
      ...option,
      height: IMAGE_GRID_HEIGHT,
      width: gridWidth / 2,
    };
  }

  if (isFirst) {
    return {
      ...option,
      height: IMAGE_GRID_HEIGHT,
      width: gridWidth * (2 / 3),
    };
  }
  return {
    ...option,
    height: IMAGE_GRID_HEIGHT / 2,
    width: gridWidth / 3,
  };
};

export const FlashSaleImageGridV2: React.FC<IProps> = (props) => {
  const { images, useLazyImageLoading } = props;
  const isFlashDealsRevamp = useSettingDealsHub();

  const ref = useRef<HTMLDivElement>(null);
  const [gridWidth, setGridWidth] = useState(0);

  useEffect(() => {
    const getWidth = () => ref.current?.offsetWidth ?? 0;

    const handleResize = debounceIfPossible(() => {
      if (getWidth() !== gridWidth) {
        setGridWidth(getWidth());
      }
    }, 200);

    if (ref.current) {
      setGridWidth(getWidth());
    }

    getWindow()?.addEventListener("resize", handleResize);

    return () => {
      getWindow()?.removeEventListener("resize", handleResize);
    };
  }, [ref, gridWidth]);

  const numImages = Math.min(images.length, 3);
  const numAdditionalItems = Math.max(0, images.length - 3);

  const isSSR = useIsSSR();

  return (
    <Grid $numImages={numImages} ref={ref}>
      {gridWidth > 1
        ? images.slice(0, numImages).map((image, index) => {
            const isFirst = index === 0 && numImages === 3;
            const imageOptions = getImageOptions(numImages, isFirst, gridWidth);
            return (
              <ImageWrapper
                $isFirst={isFirst}
                key={image.token}
                $maxHeight={imageOptions.height ?? IMAGE_GRID_HEIGHT}
                $maxWidth={imageOptions.width ?? gridWidth}
              >
                <TextOverlay
                  numAdditionalItems={numAdditionalItems}
                  showOverlay={numAdditionalItems > 0 && index === 2}
                />
                {isFlashDealsRevamp ? (
                  <ImageOverlay
                    showOverlay={index !== 2 && numAdditionalItems > 0}
                  />
                ) : null}
                <img
                  src={optimizeProductImageUrl(image, isSSR, 200, imageOptions)}
                  loading={useLazyImageLoading ? "lazy" : undefined}
                />
              </ImageWrapper>
            );
          })
        : null}
    </Grid>
  );
};

const ImageOverlay: React.FC<{ showOverlay: boolean }> = ({ showOverlay }) => {
  if (!showOverlay) {
    return null;
  }

  return (
    <TransparencyOverlay
      style={{
        zIndex: 2,
        display: "block",
        background: Core.surface.enabled,
        opacity: 0.02,
      }}
    />
  );
};

const Overlay = styled(TransparencyOverlay)`
  z-index: 2;
  display: block;
  background: ${Core.text.enabled + Opacity.O_35};
`;

const OverlayTextContainer = styled(Row).attrs({
  justify: "center",
  align: "center",
})`
  height: 100%;
  width: 100%;
`;

const getGridSize = (numImages: number) => {
  if (numImages === 1) {
    return {
      columns: "1 fr",
      rows: "1 fr",
    };
  }
  if (numImages === 2) {
    return {
      columns: "1fr 1fr",
      rows: "1 fr",
    };
  }
  return {
    columns: "2fr 1fr",
    rows: "1fr 1fr",
  };
};

const Grid = styled.div<{ $numImages: number }>`
  display: grid;
  grid-gap: 2px;
  flex-shrink: 0;
  height: ${IMAGE_GRID_HEIGHT}px;
  ${({ $numImages }) =>
    css`
      grid-template-columns: ${getGridSize($numImages).columns};
      grid-template-rows: ${getGridSize($numImages).rows};
    `}
`;

const ImageWrapper = styled.div<{
  $isFirst: boolean;
  $maxWidth: number;
  $maxHeight: number;
}>`
  width: 100%;
  height: 100%;
  position: relative;

  ${(props) => (props.$isFirst ? "grid-row: 1 / span 2;" : "")}
  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
    max-width: ${(props) => props.$maxWidth}px;
    max-height: ${(props) => props.$maxHeight}px;
  }
`;
